import { eventsFeature, JoiEvent } from './../store/events.era';
import { collection, doc, DocumentChange, DocumentData, DocumentSnapshot, Firestore, query, setDoc, updateDoc } from '@angular/fire/firestore';
import { environment } from '../../../src/environments/environment';
import { LogService } from './log.service';
import { Subject } from 'rxjs';
import { Injectable } from "@angular/core";
import { Store, select } from '@ngrx/store';
import { OnReset, Transitions } from './transition.service.helpers';
import { Dictionary } from '@ngrx/entity';
import { budgetActions } from 'src/app/store/budget.era';

import { SubscriptionHelper } from './helpers/subscriptions.helper';
import { ShareableItem } from 'globals/store/share.data';
import { ShareService } from './share.service';
import { accountActions, accountsFeature } from '../store/accounts.era';
import { showBudgetReselector } from '../store/reselectors/permissions.reselector';
import { JoiAccount } from 'globals/store/accounts.data';
import { Budget, createDefaultBudget } from 'globals/store/budget.data';
import { updateBudgetInPlace } from 'globals/helpers/budget.helper';
import { jsonCopyObject } from 'globals/helpers/helpers';
import { subCallback, subTilDestroy } from '../helpers/helpers';
import { getOneStorePromise } from '../store/ui.era';
// import { where } from 'firebase/firestore';
import { where } from '@angular/fire/firestore';

@Injectable({ providedIn: 'root' })
export class BudgetService implements OnReset {

  readonly _file: string = 'budget.service.ts';
  readonly api = `${environment.functionsURL}api/app`;
  
  public $budgetDestroy: Subject<boolean> = new Subject()
  public events: Dictionary<JoiEvent>
  public $destroy_budgets: Subject<boolean> = new Subject()
  attempted = false
  createdNewBudget = false
  public $destroy: Subject<boolean> = new Subject()
  subscribedAccountIds: Set<string> = new Set()

  onReset(resetType, value = null) {
    if (resetType === Transitions.OnExitEvent) {
      this.resetEvent()
    }
    if (resetType === Transitions.OnExitUser) {
      this.resetEvent()
      this.resetAccounts()
    }
  }

  resetEvent() {
    this.$budgetDestroy.next(true)
    this.attempted = false
    this.createdNewBudget = false
  }

  resetAccounts() {
    this.$destroy_budgets.next(true)
    this.subscribedAccountIds = new Set()
    this.store.dispatch(accountActions.selectBudgetAccount(null)) // this is not happening anywhere else, need to reset so can be set again when returning to home => budget
  }

  constructor(
    private db: Firestore,
    private log: LogService,
    private shareService: ShareService,
    private subscriptionHelper: SubscriptionHelper,
    public store: Store) {
    subTilDestroy(this, select(eventsFeature.selectEntities), 'events')
    subCallback(this, this.store.select(showBudgetReselector), async(data) => {
      // console.log({ data })
      if (data && !this.attempted) {
        // console.log(data)
        this.attempted = true
        const { event } = data

        let account: JoiAccount
        while (!account || account.id !== event.accountId) {
          account = await getOneStorePromise(this.store, select(accountsFeature.selectAccount))
        }
        this.getBudget(event.eventId, account)
      }
    })
    subCallback(this, this.store.select(eventsFeature.selectEvents_unloading), is_unloading => {
      if(is_unloading) {
        this.resetAccounts()
      }
    })
    subCallback(this, this.store.select(accountsFeature.selectSelectedBudgetAccount), accountId => {
      if (accountId && !this.subscribedAccountIds.has(accountId)) {
        this.getAccountBudgets(accountId)
        this.subscribedAccountIds.add(accountId)
      }
    })
    
  }

  updateBudget(eventId, budget: Budget) { // budget needs to be complete to update properly in place, although we could move it to soley incoming updating in place if necessary
    console.log("Saving budget...", jsonCopyObject(budget))
    const ref = doc(this.db,`budgets/${eventId}`)
    updateBudgetInPlace(budget)

    return updateDoc<any, any>(ref, budget).catch(response => {
      this.log.error({
          file:this._file,
          function:'updateBudget',
          response,
          data: { eventId, budget },
          error: new Error,
          message: 'There was an error updating the budget'
      });
    })
  }

  
  loadAccountBudgetsFromDB(changes:DocumentChange<Budget>[]) {
    // console.log({ changes })
    const deletedIds = changes.filter(c => c.type === 'removed').map(c => c.doc.id)
    if (deletedIds.length) this.store.dispatch(budgetActions.removeAccountBudgets(deletedIds))

    const addedOrModifiedBudgets = changes.filter(c => c.type !== 'removed').map(c => c.doc.data())
    if (addedOrModifiedBudgets.length) this.store.dispatch(budgetActions.loadAccountBudgets(addedOrModifiedBudgets))

    // console.log({ deletedIds, addedOrModifiedBudgets })    
  }

  getAccountBudgets(accountId: string) {
    // console.log("Get account budgets", { accountId })
    const budgetsCol = query<any, DocumentData>(collection(this.db, 'budgets'), where('accountId', '==', accountId))
    this.subscriptionHelper.subscribeToCollectionsWithHandlerAndStopper(budgetsCol, 'budgets', this.loadAccountBudgetsFromDB.bind(this), this.$destroy_budgets, this._file, 'getAccountBudgets:budgets')
    const quoteInfosCol = query<any, DocumentData>(collection(this.db, 'shareables'), where('accountId', '==', accountId), where('type', '==', 'quote'))
    this.subscriptionHelper.subscribeToCollectionsWithHandlerAndStopper<ShareableItem>(quoteInfosCol, 'shareables', this.shareService.handleShareableChangesFromDB.bind(this.shareService), this.$destroy_budgets, this._file, 'getAccountBudgets:quoteInfos')
  }

  async getBudget(eventId: string, account: JoiAccount) {
    const changesHandler = (changes:  DocumentSnapshot<Budget>) => {
      let budget: Budget
      if (!changes.exists) {
        if (this.createdNewBudget) return
        this.createdNewBudget = true
        
        budget = createDefaultBudget(eventId, account)
        console.log("NO BUDGET! CREATING...", budget)
        updateBudgetInPlace(budget)
        setDoc(doc(this.db, 'budgets', eventId), budget)
                      .catch(err => {
                        window['budgetError'] = err
                        console.error(err)
                      })
      } else budget = changes.data()
      updateBudgetInPlace(budget)
      this.store.dispatch(budgetActions.loadBudget(budget))
    }
    this.subscriptionHelper.subscribeToDocumentSnapshotChanges(doc(this.db, 'budgets', eventId), 
      changesHandler.bind(this), this.$budgetDestroy, this._file, 'getBudget')
  }

}
